/**
 * The card terminal.
 *
 * Stays on screen for the whole authorisation — 120 seconds is normal, because
 * a customer inserting a card, choosing an account and entering a PIN takes as
 * long as it takes. What must never happen is a dismissal that loses an
 * approval, so the only way out while a charge is in flight is Cancel, which
 * tells the terminal.
 *
 * **The wait has a shape.** It used to be an unbounded spinner, which for two
 * minutes is indistinguishable from a frozen kiosk — and a customer who decides
 * the machine has crashed while their card is in a terminal is the person most
 * likely to walk away from a charge that is about to succeed. A bar that fills
 * against the terminal's own typical window says "this is taking the time it
 * takes" without promising a deadline the kiosk does not control.
 *
 * It deliberately does **not** time anything out. The bar reaching its end
 * means the authorisation is slower than usual, nothing more; only the terminal
 * or the customer may end a charge.
 */
import { useEffect, useState } from 'react'
import { format } from '@mgc/domain'
import type { Money } from '@mgc/domain'
import { Button, Plaque, Sheet, Spinner, Total } from '@/components/ui'
import { useT } from '@/i18n'
import { cancelTerminal, shellInfo } from '@/shell/native'

/**
 * The window a card authorisation normally lands inside.
 *
 * Presentation only. `shell_terminal_pay` owns the real timeout, and this bar
 * has no authority over it — the number is here so the customer can see that
 * something with a duration is happening, not so the page can decide when to
 * give up.
 */
const TYPICAL_MS = 120_000

export function TerminalModal({
  open,
  amount,
  onPay,
  onCancel
}: {
  open: boolean
  amount: Money
  onPay: (terminalId: string) => Promise<void> | void
  onCancel: () => void
}) {
  const t = useT()
  const [terminals, setTerminals] = useState<readonly string[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [charging, setCharging] = useState(false)
  /** Milliseconds since the charge started, for the progress bar alone. */
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!charging) {
      setElapsed(0)
      return
    }
    const startedAt = Date.now()
    const tick = setInterval(() => setElapsed(Date.now() - startedAt), 500)
    return () => clearInterval(tick)
  }, [charging])

  useEffect(() => {
    if (!open) return
    // The ids come from `kiosk.toml`, which is the machine's own file — the
    // page never names a terminal the shell did not report. Closes O8.
    void shellInfo().then((info) => {
      const ids = info?.terminals ?? []
      setTerminals(ids)
      // One terminal is the normal case and needs no question asked.
      if (ids.length === 1) setChosen(ids[0]!)
    })
  }, [open])

  useEffect(() => {
    if (!open || chosen === null || charging) return
    setCharging(true)
    void Promise.resolve(onPay(chosen)).finally(() => setCharging(false))
  }, [open, chosen])

  const cancel = async () => {
    if (chosen !== null) await cancelTerminal(chosen).catch(() => {})
    onCancel()
  }

  return (
    <Sheet open={open} labelledBy="terminal-title">
      <div className="flex flex-col items-center gap-8 p-12 text-center">
        {terminals !== null && terminals.length === 0 ? (
          <p className="display-upright text-[length:var(--text-kiosk-lg)]">{t('payment.notConfigured')}</p>
        ) : chosen === null ? (
          <>
            <Plaque icon="card" size="large" />
            <h2 id="terminal-title" className="display text-[length:var(--text-kiosk-xl)] uppercase">
              {t('settings.terminal')}
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {(terminals ?? []).map((id) => (
                <Button key={id} onClick={() => setChosen(id)}>
                  {id}
                </Button>
              ))}
            </div>
          </>
        ) : (
          <>
            <Plaque icon="card" size="large" />
            <h2 id="terminal-title" className="display text-[length:var(--text-kiosk-xl)] uppercase">
              {t('payment.terminalWaiting')}
            </h2>

            {/*
              The panel a customer looks at while the terminal beside them does
              the work. It is the dark surface again — the same one the total
              arrived on — with the amount on it and a spinner that does not
              stop until the terminal answers.
            */}
            <div className="flex w-full max-w-md flex-col items-center gap-8 rounded-[var(--radius-tile)] bg-[var(--color-dark)] p-12 shadow-[var(--shadow-tile)]">
              <Total size="large" value={format(amount)} />
              <Spinner tone="brand" />

              {/*
                Capped at 100% and never removed. Past the typical window the
                bar simply sits full while the spinner keeps turning, because
                the alternative — a bar that empties, or a message announcing
                failure — would be the page claiming an outcome only the
                terminal knows.
              */}
              <div className="w-full">
                <div className="h-3 w-full overflow-hidden rounded-[var(--radius-pill)] bg-white/15">
                  <div
                    className="h-full rounded-[var(--radius-pill)] bg-[var(--color-brand)] transition-[width] duration-500 ease-linear"
                    style={{ width: `${Math.min(100, (elapsed / TYPICAL_MS) * 100)}%` }}
                  />
                </div>
                <p className="eyebrow mt-3 text-white/40">{t('payment.terminalElapsed')}</p>
              </div>

              <p className="eyebrow text-white/40">{t('payment.terminalHint')}</p>
            </div>
          </>
        )}

        <Button variant="ghost" onClick={() => void cancel()}>
          {t('payment.cancel')}
        </Button>
      </div>
    </Sheet>
  )
}
